import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip,
  CartesianGrid, ResponsiveContainer, Cell,
} from "recharts";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { api, setMgrSite } from "@/lib/api";
import { TrendingUp, Clock, Calendar, Heart, MapPin, ArrowUpRight, Wallet } from "lucide-react";
import { UpcomingHolidaysCard } from "@/components/UpcomingHolidaysCard";
import { LocationCheckCard } from "@/components/LocationCheckCard";
import { ComplianceCard } from "@/components/ComplianceCard";
import { Pagination, usePagination } from "@/components/Pagination";

const NAVY = "#1A2744";
const GOLD = "#C9A84C";

const SITE_OPTIONS = [
  { value: "ALL", label: "All sites" },
  { value: "SITE_1", label: "Christchurch" },
  { value: "SITE_2", label: "Wellington" },
];

const SITE_LABEL = { SITE_1: "Christchurch", SITE_2: "Wellington", ALL: "All sites" };

function KpiCard({ label, value, suffix, subtle, testid, icon: Icon, dark }) {
  return (
    <Card className={dark ? "bg-[#1A2744] border-0 text-white" : "border-gray-200 bg-white"}>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <span className={`text-[10px] uppercase tracking-widest font-semibold ${dark ? "text-[#C9A84C]" : "text-gray-500"}`}>
            {label}
          </span>
          {Icon && <Icon className={`h-4 w-4 ${dark ? "text-[#C9A84C]" : "text-[#C9A84C]"}`} strokeWidth={1.5} />}
        </div>
        <div className={`font-heading text-4xl font-bold mt-3 ${dark ? "text-white" : "text-[#1A2744]"}`} data-testid={testid}>
          {value}
          {suffix && <span className="text-lg font-medium opacity-70 ml-1">{suffix}</span>}
        </div>
        {subtle && <div className={`text-xs mt-1 ${dark ? "text-white/60" : "text-gray-500"}`}>{subtle}</div>}
      </CardContent>
    </Card>
  );
}

function fmtDayLabel(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString([], { weekday: "short", day: "numeric" });
}

const NZ_CURRENCY = new Intl.NumberFormat("en-NZ", {
  style: "currency",
  currency: "NZD",
  maximumFractionDigits: 2,
});
const fmtNZD = (v) => NZ_CURRENCY.format(Number(v) || 0);

const LEAVE_TYPE_LABEL = {
  ANNUAL: "Annual Holidays",
  ALTERNATIVE_HOLIDAYS: "Alternative Holidays",
};

function fmtWeekLabel(iso) {
  const d = new Date(iso);
  return `${d.getDate()}/${d.getMonth() + 1}`;
}

function fmtMonthLabel(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString([], { month: "short" });
}

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [site, setSite] = useState("ALL");

  const load = useCallback(async (siteValue) => {
    const params = siteValue && siteValue !== "ALL" ? { site: siteValue } : {};
    const { data } = await api.get("/admin/analytics", { params });
    setData(data);
  }, []);

  useEffect(() => {
    load(site).catch(() => {});
    // Keep Manager-module scope in sync — if the admin later clicks the
    // "Manager" nav tab, the same site will already be pre-selected.
    setMgrSite(site);
    const t = setInterval(() => load(site).catch(() => {}), 60000);
    return () => clearInterval(t);
  }, [load, site]);

  if (!data) return <div className="text-sm text-gray-500">Loading analytics…</div>;

  const lt = data.leave_totals;

  return (
    <AdminDashboardInner data={data} site={site} setSite={setSite} lt={lt} />
  );
}

function AdminDashboardInner({ data, site, setSite, lt }) {
  const empWeekPg = usePagination(data.per_employee_week, 10);
  const empLeavePg = usePagination(data.per_employee_leave, 10);

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs text-[#C9A84C] uppercase tracking-[0.25em] font-semibold">Analytics</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-[#1A2744] mt-1">How the café is running</h1>
          <p className="text-gray-500 mt-2">
            Hours worked, team workload, and leave balances
            {site === "ALL" ? " across both sites." : ` at ${SITE_LABEL[site]}.`}
          </p>
        </div>
        <div className="flex items-center gap-2 self-start sm:self-end">
          <MapPin className="h-4 w-4 text-[#C9A84C]" strokeWidth={1.8} />
          <Select value={site} onValueChange={setSite}>
            <SelectTrigger className="w-48 h-10 border-[#1A2744]/20 bg-white" data-testid="analytics-site-filter">
              <SelectValue placeholder="All sites" />
            </SelectTrigger>
            <SelectContent>
              {SITE_OPTIONS.map((o) => (
                <SelectItem
                  key={o.value}
                  value={o.value}
                  data-testid={`analytics-site-option-${o.value.toLowerCase()}`}
                >
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            asChild
            variant="outline"
            size="sm"
            className="h-10 gap-1.5 border-[#1A2744]/20 text-[#1A2744] hover:bg-[#C9A84C]/10"
            data-testid="open-manager-view-btn"
            title={`Open the Manager module pre-filtered to ${site === "ALL" ? "all sites" : SITE_LABEL[site]}`}
          >
            <Link to="/admin/manager/dashboard">
              Open in Manager
              <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </Button>
        </div>
      </header>

      {/* KPI row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Today" value={data.today_hours} suffix="hrs" testid="kpi-today" icon={Clock} dark />
        <KpiCard label="This week" value={data.week_hours} suffix="hrs" testid="kpi-week" icon={TrendingUp} />
        <KpiCard label="This month" value={data.month_hours} suffix="hrs" testid="kpi-month" icon={Calendar} />
        <KpiCard
          label="Leave liability"
          value={fmtNZD(data.leave_liability?.total)}
          subtle={
            data.leave_liability?.employees_without_rate > 0
              ? `${data.leave_liability.employees_without_rate} employee${data.leave_liability.employees_without_rate === 1 ? "" : "s"} excluded · no rate`
              : "Annual + Alt Holidays"
          }
          testid="kpi-leave-liability"
          icon={Wallet}
        />
      </div>

      {/* Compliance dashboard — visa expiries, trial reviews due, document gaps */}
      <ComplianceCard />

      {/* Leave liability breakdown — projected $ paid-out exposure across
          Annual + Alternative Holidays. Site filter respects the page-level
          filter (uses the analytics endpoint's filtered totals). */}
      {data.leave_liability && (
        <Card data-testid="leave-liability-breakdown">
          <CardContent className="p-6">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div>
                <h3 className="font-heading text-xl font-bold text-[#1A2744] flex items-center gap-2">
                  <Wallet className="h-4 w-4 text-[#C9A84C]" strokeWidth={1.8} /> Leave liability
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Projected paid-out exposure for active employees · Annual + Alternative Holidays · remaining hours @ each employee&apos;s hourly rate
                </p>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">Total</div>
                <div className="font-heading text-3xl font-bold text-[#1A2744]" data-testid="liability-total">
                  {fmtNZD(data.leave_liability.total)}
                </div>
                {data.leave_liability.employees_without_rate > 0 && (
                  <div className="text-[11px] text-amber-700 mt-1" data-testid="liability-missing-rates">
                    {data.leave_liability.employees_without_rate} employee
                    {data.leave_liability.employees_without_rate === 1 ? "" : "s"} excluded — no hourly rate set
                  </div>
                )}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold mb-2">By leave type</div>
                <div className="space-y-2">
                  {Object.entries(data.leave_liability.by_type || {}).map(([lt, v]) => (
                    <div
                      key={lt}
                      className="flex items-center justify-between border-b border-gray-100 pb-1.5"
                      data-testid={`liability-type-${lt}`}
                    >
                      <span className="text-sm text-[#1A2744]">{LEAVE_TYPE_LABEL[lt] || lt}</span>
                      <span className="font-mono font-semibold text-[#1A2744]">{fmtNZD(v)}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold mb-2">By site</div>
                <div className="space-y-2">
                  {Object.entries(data.leave_liability.by_site || {}).map(([s, v]) => (
                    <div
                      key={s}
                      className="flex items-center justify-between border-b border-gray-100 pb-1.5"
                      data-testid={`liability-site-${s}`}
                    >
                      <span className="text-sm text-[#1A2744]">{SITE_LABEL[s] || s}</span>
                      <span className="font-mono font-semibold text-[#1A2744]">{fmtNZD(v)}</span>
                    </div>
                  ))}
                  {Object.keys(data.leave_liability.by_site || {}).length === 0 && (
                    <p className="text-xs text-gray-500">No site breakdown available.</p>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Daily bar chart */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-xl font-bold text-[#1A2744]">Hours — last 14 days</h3>
            <span className="text-xs text-gray-500">Daily totals across all employees</span>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.daily}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
                <XAxis dataKey="date" tickFormatter={fmtDayLabel} stroke="#9CA3AF" fontSize={11} />
                <YAxis stroke="#9CA3AF" fontSize={11} />
                <Tooltip
                  formatter={(v) => [`${v} hrs`, "Hours"]}
                  labelFormatter={(l) => new Date(l).toLocaleDateString([], { weekday: "long", day: "numeric", month: "short" })}
                  contentStyle={{ background: NAVY, border: "none", borderRadius: 8, color: "white" }}
                />
                <Bar dataKey="hours" radius={[6, 6, 0, 0]}>
                  {data.daily.map((d, i) => (
                    <Cell key={`cell-${d.date || i}`} fill={i === data.daily.length - 1 ? GOLD : NAVY} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Weekly + Monthly charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-xl font-bold text-[#1A2744]">Weekly trend</h3>
              <span className="text-xs text-gray-500">Last 8 weeks</span>
            </div>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data.weekly}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
                  <XAxis dataKey="week_start" tickFormatter={fmtWeekLabel} stroke="#9CA3AF" fontSize={11} />
                  <YAxis stroke="#9CA3AF" fontSize={11} />
                  <Tooltip
                    formatter={(v) => [`${v} hrs`, "Hours"]}
                    labelFormatter={(l) => `Week of ${new Date(l).toLocaleDateString([], { day: "numeric", month: "short" })}`}
                    contentStyle={{ background: NAVY, border: "none", borderRadius: 8, color: "white" }}
                  />
                  <Line type="monotone" dataKey="hours" stroke={GOLD} strokeWidth={3} dot={{ fill: NAVY, r: 4 }} activeDot={{ r: 6 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-xl font-bold text-[#1A2744]">Monthly trend</h3>
              <span className="text-xs text-gray-500">Last 6 months</span>
            </div>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.monthly}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
                  <XAxis dataKey="month" tickFormatter={fmtMonthLabel} stroke="#9CA3AF" fontSize={11} />
                  <YAxis stroke="#9CA3AF" fontSize={11} />
                  <Tooltip
                    formatter={(v) => [`${v} hrs`, "Hours"]}
                    labelFormatter={(l) => new Date(l).toLocaleDateString([], { month: "long", year: "numeric" })}
                    contentStyle={{ background: NAVY, border: "none", borderRadius: 8, color: "white" }}
                  />
                  <Bar dataKey="hours" fill={NAVY} radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Per-employee weekly hours */}
      <Card>
        <CardContent className="p-0">
          <div className="p-6 pb-3 flex items-center justify-between">
            <h3 className="font-heading text-xl font-bold text-[#1A2744]">Per-employee hours this week</h3>
            <span className="text-xs text-gray-500">{data.per_employee_week.length} employees</span>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead className="text-right">Hours this week</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {empWeekPg.pageItems.map((e) => (
                  <TableRow key={e.user_id} data-testid={`row-emp-week-${e.user_id}`}>
                    <TableCell className="font-medium">{e.name}</TableCell>
                    <TableCell className="text-xs text-gray-500 font-mono">{e.employee_code || "—"}</TableCell>
                    <TableCell className="text-right">
                      <span className="font-heading text-lg font-bold text-[#1A2744]">{e.hours}</span>
                      <span className="text-xs text-gray-400 ml-1">hrs</span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="px-6 pb-4">
            <Pagination
              page={empWeekPg.page}
              pageCount={empWeekPg.pageCount}
              total={empWeekPg.total}
              pageSize={empWeekPg.pageSize}
              onPageChange={empWeekPg.setPage}
              idPrefix="emp-week"
            />
          </div>
        </CardContent>
      </Card>

      {/* Leave totals */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="border-gray-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">Annual leave · team total</div>
              <Calendar className="h-4 w-4 text-[#C9A84C]" strokeWidth={1.5} />
            </div>
            <div className="mt-4 grid grid-cols-3 gap-4">
              <div>
                <div className="text-xs text-gray-500">Entitled</div>
                <div className="font-heading text-2xl font-bold text-[#1A2744]" data-testid="annual-total-entitled">{lt.annual_entitled}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Used</div>
                <div className="font-heading text-2xl font-bold text-amber-600" data-testid="annual-total-used">{lt.annual_used}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Remaining</div>
                <div className="font-heading text-2xl font-bold text-emerald-600" data-testid="annual-total-remaining">{lt.annual_remaining}</div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-gray-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">Sick leave · team total</div>
              <Heart className="h-4 w-4 text-[#C9A84C]" strokeWidth={1.5} />
            </div>
            <div className="mt-4 grid grid-cols-3 gap-4">
              <div>
                <div className="text-xs text-gray-500">Entitled</div>
                <div className="font-heading text-2xl font-bold text-[#1A2744]" data-testid="sick-total-entitled">{lt.sick_entitled}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Used</div>
                <div className="font-heading text-2xl font-bold text-amber-600" data-testid="sick-total-used">{lt.sick_used}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Remaining</div>
                <div className="font-heading text-2xl font-bold text-emerald-600" data-testid="sick-total-remaining">{lt.sick_remaining}</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Per-employee leave */}
      <Card>
        <CardContent className="p-0">
          <div className="p-6 pb-3 flex items-center justify-between">
            <h3 className="font-heading text-xl font-bold text-[#1A2744]">Per-employee leave</h3>
            <span className="text-xs text-gray-500">Balances in hours</span>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead rowSpan={2} className="align-bottom">Employee</TableHead>
                  <TableHead colSpan={3} className="text-center border-l border-gray-100">Annual</TableHead>
                  <TableHead colSpan={3} className="text-center border-l border-gray-100">Sick</TableHead>
                </TableRow>
                <TableRow>
                  <TableHead className="text-right border-l border-gray-100">Entitled</TableHead>
                  <TableHead className="text-right">Used</TableHead>
                  <TableHead className="text-right">Remaining</TableHead>
                  <TableHead className="text-right border-l border-gray-100">Entitled</TableHead>
                  <TableHead className="text-right">Used</TableHead>
                  <TableHead className="text-right">Remaining</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {empLeavePg.pageItems.map((e) => (
                  <TableRow key={e.user_id} data-testid={`row-emp-leave-${e.user_id}`}>
                    <TableCell className="font-medium">
                      {e.name}
                      <div className="text-[10px] text-gray-500 font-mono">{e.employee_code || ""}</div>
                    </TableCell>
                    <TableCell className="text-right border-l border-gray-100">{e.annual_entitled}</TableCell>
                    <TableCell className="text-right text-amber-600">{e.annual_used}</TableCell>
                    <TableCell className="text-right text-emerald-600 font-semibold">{e.annual_remaining}</TableCell>
                    <TableCell className="text-right border-l border-gray-100">{e.sick_entitled}</TableCell>
                    <TableCell className="text-right text-amber-600">{e.sick_used}</TableCell>
                    <TableCell className="text-right text-emerald-600 font-semibold">{e.sick_remaining}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="px-6 pb-4">
            <Pagination
              page={empLeavePg.page}
              pageCount={empLeavePg.pageCount}
              total={empLeavePg.total}
              pageSize={empLeavePg.pageSize}
              onPageChange={empLeavePg.setPage}
              idPrefix="emp-leave"
            />
          </div>
        </CardContent>
      </Card>

      {/* Upcoming public holidays — honours the site filter */}
      <UpcomingHolidaysCard
        site={site === "ALL" ? null : site}
        limit={6}
        testid="admin-upcoming-holidays"
      />

      {/* Troubleshoot staff access (geo + cafe Wi-Fi) */}
      <LocationCheckCard />
    </div>
  );
}
